import ChatApiService from "@api-services/chat/ChatApiService";
import AgencyUserApiService from "@api-services/user/AgencyUserApiService";
import {
	ActiveSharedBetSlip,
	ChannelInfoDataFromList,
	ChannelInfoDataFromOffer,
	ChannelInfoDataFromSession,
	ChatEntryPoint,
	ChatWindowState,
	SelectedReplyOnMessage,
} from "@data-types/chat";
import { Bet } from "@models";
import { ChatSDKService } from "@services/chat";
import { localizationService } from "@state/services";
import { analyticsTracker } from "@utils/analytics";
import { DateTime } from "luxon";
import { action, computed, observable, runInAction } from "mobx";

import { LoaderStore } from "../common";
import { RootStore } from "../types";
import { ChannelListHandler } from "./ChannelListHandler";
import { ChannelSessionHandler } from "./ChannelSessionHandler";
import { ChatApiError } from "./ChatApiErrorModel";
import { ChatBetSlipShareHandler } from "./ChatBetSlipShareHandler";
import { ChatChannelHandler } from "./ChatChannelHandler";
import { ChatClientHandler } from "./ChatClientHandler";

export class ChatViewStore {
	rootStore: RootStore;
	chatLoader: LoaderStore;
	chatClientHandler: ChatClientHandler;
	chatChannelHandler: ChatChannelHandler;
	chatListHandler: ChannelListHandler;
	channelSessionHandler: ChannelSessionHandler;
	chatBetSlipShareHandler: ChatBetSlipShareHandler;

	entryPoint: ChatEntryPoint | null;
	channelData:
		| ChannelInfoDataFromOffer
		| ChannelInfoDataFromList
		| ChannelInfoDataFromSession
		| null;

	@observable sharedBetSlipData: ActiveSharedBetSlip | null;

	@observable activeImagePreview: string | null = null;

	@observable currentHighlightedMessageId: string;

	//flag for showing chat box
	@observable chatBoxVisible: boolean = false;

	//flag for showing chat box and chat button
	@observable chatWidgetVisible: boolean = false;

	//flag for showing chat list
	@observable chatListVisible: boolean = false;

	@observable errorMessage: string | null;

	@observable leaveChannelDropdownOpened: boolean = false;

	@observable isDisposing: boolean = false;

	@observable selectedMessageToReplyOn: SelectedReplyOnMessage | null = null;

	@observable replyScrollingLoader: boolean = false;

	@observable channelUnreadObserver: Amity.Unsubscriber;

	@observable unreadSupportMessages: boolean;

	@observable showChatNameForm: boolean;

	@observable supportChatInfoPopup: boolean;

	@observable supportChannelId: string | null;

	@observable imagesDoneLoading = false;

	@observable isSupportChatLinkBoxDismissed: boolean;

	@action.bound
	setReplyScrollingLoader(value: boolean) {
		this.replyScrollingLoader = value;
	}

	@action.bound
	setImagesDoneLoading(value: boolean) {
		this.imagesDoneLoading = value;
	}

	@action.bound
	setIsSupportChatLinkDismissed(value: boolean) {
		this.isSupportChatLinkBoxDismissed = value;
	}

	@action.bound
	openImagePreview(imageUrl: string | null) {
		this.activeImagePreview = imageUrl;
	}

	@action.bound
	closeImagePreview() {
		this.activeImagePreview = null;
	}

	@computed get chatDisplayName(): string | undefined {
		return this.rootStore.userAuthStore.user?.chatName;
	}

	@computed get username() {
		return this.rootStore.userAuthStore.user?.username;
	}

	//loaders
	@computed get channelListLoader() {
		//triggered when fetching channel list
		return this.chatListHandler.loaderStore.isLoadingProcess;
	}

	@computed get messageSendingLoader() {
		//triggered when sending message in a channel
		return this.chatChannelHandler.messageLoader.isLoadingProcess;
	}

	@computed get flaggingLoader() {
		//triggered when trying to flag message
		return this.chatChannelHandler.flaggingLoader.isLoading;
	}

	@computed get channelInfoLoader() {
		return this.chatChannelHandler.channelInfoLoader.isLoadingProcess;
	}

	@computed get messageListLoader() {
		return this.chatChannelHandler.messageListLoader.isLoadingProcess;
	}

	@computed get shouldRenderMainLoader() {
		return (
			this.chatLoader.isLoadingProcess ||
			this.chatChannelHandler.messageListLoader.isLoadingProcess ||
			this.channelListLoader
		);
	}

	@computed get chatClient() {
		return this.chatClientHandler.currentClient;
	}

	@computed get chatBetSlipShareData() {
		return this.chatBetSlipShareHandler.activePopupBet;
	}

	@computed get imagePreview() {
		return this.activeImagePreview;
	}

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;

		this.chatClientHandler = new ChatClientHandler(this);

		this.chatChannelHandler = new ChatChannelHandler(this);

		this.chatListHandler = new ChannelListHandler(this);

		this.channelSessionHandler = new ChannelSessionHandler(this);

		this.chatLoader = new LoaderStore();

		this.chatBetSlipShareHandler = new ChatBetSlipShareHandler(this);
	}

	async updateChatOnLoginChange(isUserLoggedIn: boolean) {
		if (isUserLoggedIn) {
			this.initializeChatClient();
			this.channelSessionHandler.handleJoiningChannelFromSession();
		} else {
			this.disposeUnreadMessageObserver();
			this.channelSessionHandler.removeSessionChannelInfo();
			this.chatClientHandler.disconnectUser();
			await this.closeChat();
		}
	}

	async onInitialize(
		entryPoint: ChatEntryPoint,
		channelData:
			| ChannelInfoDataFromOffer
			| ChannelInfoDataFromList
			| ChannelInfoDataFromSession
			| null
	) {
		if (this.isDisposing) {
			//client and session still disposing
			return;
		}

		if (!WEBPACK_IS_CHAT_AVAILABLE) {
			//do not initiate chat
			return;
		}

		if (!this.username) {
			//user not logged in
			this.rootStore.notificationStore.info(
				localizationService.t("CHAT.ERR_HANDLING.PLEASE_LOGIN")
			);
			return;
		}

		analyticsTracker.trackOpenChatInteraction();

		const shouldUpdateChatName = this.checkForUserChatName(
			entryPoint,
			channelData,
			this.chatDisplayName
		);

		if (shouldUpdateChatName) {
			//updating chat name screen
			return;
		}

		if (
			this.chatWidgetVisible &&
			this.chatClient?.userId &&
			ChatEntryPoint.USER_MENU == entryPoint
		) {
			//if chat is visible, user is logged in into sdk client and entry point is user menu (quick link or user menu)
			this.toggleChatBox(true);
			return;
		}

		//trigger bet details store initializing,
		//(cannot do it in constructor due to betdetailsstore requiring offer store which is undefined in that moment)
		this.chatBetSlipShareHandler.initializeBetDetailsStore();

		try {
			this.chatLoader.suspend();

			if (entryPoint != ChatEntryPoint.SUPPORT) {
				this.toggleChatWidget(true);
				if (
					entryPoint !== ChatEntryPoint.SESSION ||
					(channelData &&
						"chatState" in channelData &&
						channelData.chatState !== ChatWindowState.MINIMIZED)
				) {
					this.toggleChatBox(true);
				}
			}

			await this.chatClientHandler.handleClient();

			if (this.chatChannelHandler.disposerList.length > 0) {
				this.chatChannelHandler.disposeChannelHandler();
			}

			if (entryPoint == ChatEntryPoint.SUPPORT) {
				this.chatChannelHandler.addResubscribeListener();

				await this.chatChannelHandler.joinSupportChannel();
			} else {
				await this.chatListHandler.initialize(this.username);
				await this.chatChannelHandler.joinUserIntoChannelPerType(
					entryPoint,
					channelData
				);
			}

			this.channelSessionHandler.onInitialize();
			this.setSelectedMessageToReplyOn(null);
		} catch (error) {
			this.chatLoader.resume();
			this.handleError(error);
		}
	}

	@action.bound
	async initializeChatClient() {
		if (
			!WEBPACK_IS_SUPPORT_CHAT_AVAILABLE ||
			!WEBPACK_IS_CHAT_AVAILABLE ||
			!this.chatDisplayName
		) {
			//do not initialize chat client
			return;
		}

		await this.chatClientHandler.handleClient();

		if (
			!this.username ||
			!this.chatClientHandler.currentClient?.token?.accessToken
		) {
			return;
		}

		try {
			const response = await ChatApiService.getSupportChannel(
				this.username,
				this.chatClientHandler.currentClient?.token?.accessToken
			);

			if (!response.channels) {
				return;
			}

			runInAction(() => {
				const channelId = response.channels[0].channelId;
				const channelMember =
					response.channels[0].displayName.split(" - ")[2];

				if (channelMember != this.username) {
					// Support channel init cancelled: chat channel user member is not the same as logged in user
					// Support chat will init on page entry
					return;
				}

				this.chatChannelHandler.supportChannelId = channelId;

				ChatSDKService.enableUnreadCount();

				this.channelUnreadObserver = ChatSDKService.getChannelInfo(
					channelId,
					(e) => {
						if (e?.data?.unreadCount) {
							this.unreadSupportMessages = e.data.unreadCount > 0;
						}
					}
				);
			});
		} catch (error) {
			console.error(error);
		}
	}

	@action.bound
	async disposeUnreadMessageObserver() {
		this.unreadSupportMessages = false;
		this.channelUnreadObserver && this.channelUnreadObserver();
		ChatSDKService.disableUnreadCount();
	}

	async openBetSlipMessagePreview(sharedBetSlip: Bet) {
		const thumbnailData = this.mapBetSlipThumbnailData(sharedBetSlip);

		this.setSharedBetSlipData(thumbnailData);

		if (!this.chatBoxVisible && !this.chatWidgetVisible) {
			this.onInitialize(ChatEntryPoint.BET_SLIP_SHARE, null);
		} else if (!this.chatBoxVisible) {
			this.toggleChatBox(true);
		}
	}

	@action.bound
	setSharedBetSlipData(betSlipData: ActiveSharedBetSlip | null) {
		this.sharedBetSlipData = betSlipData;
	}

	@action.bound
	toggleChatWidget(value: boolean) {
		this.chatWidgetVisible = value;
	}

	@action.bound
	toggleSupportChatPopup(value: boolean) {
		this.supportChatInfoPopup = value;
	}

	@action.bound
	setIsDisposing(value: boolean) {
		this.isDisposing = value;
	}

	@action.bound
	setSelectedMessageToReplyOn(messageData: any) {
		this.selectedMessageToReplyOn = messageData;
	}

	@action.bound
	toggleChatBox(value?: boolean) {
		if (value !== undefined) {
			this.channelSessionHandler.updateChannelChatStateIntoSession({
				chatState: value
					? ChatWindowState.OPENED
					: ChatWindowState.MINIMIZED,
			});
			this.chatBoxVisible = value;
			return;
		}

		this.chatBoxVisible = !this.chatBoxVisible;
		this.channelSessionHandler.updateChannelChatStateIntoSession({
			chatState: this.chatBoxVisible
				? ChatWindowState.OPENED
				: ChatWindowState.MINIMIZED,
		});
	}

	@action.bound
	async toggleChatList(value: boolean) {
		if (value && this.username) {
			//refresh user channels list on toggle
			await this.chatListHandler.initialize(this.username);
		}
		this.chatListVisible = value;
	}

	async updateChatName(chatName: string) {
		if (this.entryPoint === null) {
			console.error(
				"CHAT ERR: Could not update chat name, entry point or channel data is null"
			);
			this.handleError(new ChatApiError(true));
			return;
		}

		try {
			await AgencyUserApiService.updateChatName(chatName);
			this.rootStore.userAuthStore.updateUserChatName(chatName);

			// If support chat link box in sports chat is dismissed before
			// creating chat name, set display status only after creating chat name
			if (this.isSupportChatLinkBoxDismissed && this.username) {
				await this.updateSupportChatLinkStatus(this.username);
			}

			await this.onInitialize(this.entryPoint, this.channelData);
		} catch (error) {
			console.error("CHAT ERR: Failed updating chat name");
			this.handleError(new ChatApiError(true));
		}
	}

	async updateSupportChatLinkStatus(username: string) {
		try {
			await ChatApiService.setSupportChatLinkStatus(username);
		} catch (error) {
			console.error(
				"CHAT ERR: Failed updating support chat info status."
			);
		}
	}

	mapBetSlipThumbnailData(sharedBetSlip: Bet): ActiveSharedBetSlip {
		let eventName: string;
		const bet = sharedBetSlip;

		const firstOfferElement = bet.betSlipOffers[0];

		const firstElementSportAbrv: string =
			firstOfferElement.sportData.sportAbrv;

		const isMultiSport = !bet.betSlipOffers.every(
			(x) => firstElementSportAbrv === x.sportData.sportAbrv
		);

		if (firstOfferElement.sportData.eventType != 0) {
			eventName =
				firstOfferElement.hasOwnProperty("isMulti") &&
				firstOfferElement.isMulti == true
					? firstOfferElement.multiEventName
					: firstOfferElement.sportData.eventName;
		} else {
			eventName = firstOfferElement.isMulti
				? firstOfferElement.multiEventName
				: [
						firstOfferElement.teamOneString,
						firstOfferElement.teamTwoString,
					].join(" - ");
		}

		return {
			id: sharedBetSlip.id,
			eventCount: sharedBetSlip.eventCount,
			maxCoefficient: sharedBetSlip.maxCoefficient,
			betSlipNumber: sharedBetSlip.betSlipNumber,
			dateCreated: DateTime.fromISO(
				sharedBetSlip.dateCreated
			).toLocaleString(DateTime.DATETIME_SHORT),
			sportAbrv: isMultiSport ? "multi" : firstElementSportAbrv,
			firstEventName: eventName,
			hasBetBuilder: sharedBetSlip.hasBetBuilder,
			bettingAccountTypeId: sharedBetSlip.bettingAccountTypeId,
		};
	}

	checkForUserChatName(
		entryPoint: ChatEntryPoint | null,
		channelData:
			| ChannelInfoDataFromOffer
			| ChannelInfoDataFromList
			| ChannelInfoDataFromSession
			| null,
		chatDisplayName: string | undefined
	): boolean {
		if (!chatDisplayName) {
			if (entryPoint !== ChatEntryPoint.SUPPORT) {
				this.toggleChatWidget(true);
				this.toggleChatBox(true);
				this.entryPoint = entryPoint;
				this.channelData = channelData;
			} else {
				this.entryPoint = entryPoint;
				this.showChatNameForm = true;
			}

			return true;
		}

		this.entryPoint = null;
		this.channelData = null;
		return false;
	}

	async leaveCurrentChannel() {
		try {
			this.chatLoader.suspend();
			//dispose listeners
			this.chatChannelHandler.disposeChannelListeners();

			const hasUserLeftTheChannel =
				await this.chatChannelHandler.leaveCurrentChannel();

			if (hasUserLeftTheChannel) {
				//dispose selected channel
				this.chatChannelHandler.disposeChannelHandler();
				this.closeChat();
			}
		} catch (error) {
			this.handleError(error);
		} finally {
			this.chatLoader.resume();
		}
	}

	handleSDKError(error: {
		errorName: string;
		isBlocking: boolean;
		localizedErrorKey: string;
	}) {
		this.errorMessage = error.localizedErrorKey;

		this.rootStore.notificationStore.error(
			localizationService.t(
				error?.localizedErrorKey || "CHAT.ERR_HANDLING.DEFAULT"
			)
		);

		if (error.isBlocking) {
			//only dispose session info on blocking error
			this.channelSessionHandler.disposeChannelSessionInfo();
			this.closeChat();
		}
	}

	@action.bound
	setLeaveChannelDropdownOpened(value: boolean) {
		this.leaveChannelDropdownOpened = value;
	}

	@action.bound
	handleError(error: any) {
		//called with catch for api calls or sdk calls
		const clientErrors = ChatSDKService.getClientErrors(error?.code);
		if (clientErrors) {
			//sdk caught an error
			this.handleSDKError(clientErrors);
			return;
		}

		//api error
		this.errorMessage = error?.message;
		this.rootStore.notificationStore.error(
			localizationService.t(error?.message || "CHAT.ERR_HANDLING.DEFAULT")
		);

		if (error?.isBlocking) {
			this.closeChat();
			//only dispose session info on blocking error
			this.channelSessionHandler.disposeChannelSessionInfo();
		}
	}

	async closeChat() {
		this.setIsDisposing(true);
		this.onDispose();
		if (this.channelSessionHandler.getChannelInfoFromSession()) {
			this.channelSessionHandler.updateChannelChatStateIntoSession({
				shouldOpenOnRefresh: false,
			});
		}
		this.chatChannelHandler.disposeChannelHandler();
		this.chatListHandler.disposeChannelList();
		this.setIsDisposing(false);
		this.setReplyScrollingLoader(false);
	}

	async closeSupportChat() {
		if (this.channelSessionHandler.getChannelInfoFromSession()) {
			this.channelSessionHandler.updateChannelChatStateIntoSession({
				shouldOpenOnRefresh: false,
			});
		}

		this.chatChannelHandler.disposeSupportChannel();

		document.removeEventListener(
			"visibilitychange",
			this.chatChannelHandler.handleVisibilityChange
		);
	}

	@action.bound
	onDispose() {
		this.chatWidgetVisible = false;
		this.chatBoxVisible = false;
		this.chatListVisible = false;
		this.sharedBetSlipData = null;
		this.selectedMessageToReplyOn = null;
		this.replyScrollingLoader = false;
		this.channelSessionHandler.disposeSessionExpiry();
		this.chatBetSlipShareHandler.onDispose();
	}
}
